import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    CheckCircle2,
    ChevronDown,
    ShieldCheck,
    ClipboardCopy,
    Pencil,
    Trash2,
    Eye,
    EyeOff,
    KeyRound,
    Link2,
    Plug,
    PlugZap,
    RefreshCw,
    TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
    Button,
    Checkbox,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Eyebrow,
    FieldLabel,
    Input,
    Mono,
    FilterBar,
    SectionHeader,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Text,
} from "@athena/ui";
import { IntegrationRecordsPanel } from "@/components/IntegrationRecordsPanel";

/** Sentinel for "no filter" — Radix Select has no unset value, only a chosen one. */
const ALL = "__all__";

const CONNECTION_STATUSES = [
    "NOT_CONFIGURED",
    "CONFIGURED",
    "CONNECTED",
    "ERROR",
] as const;

const CONNECTION_STATUS_LABEL: Record<(typeof CONNECTION_STATUSES)[number], string> = {
    NOT_CONFIGURED: "Not configured",
    CONFIGURED: "Configured",
    CONNECTED: "Connected",
    ERROR: "Error",
};
import {
    CreateIntegrationDocument,
    DeleteIntegrationDocument,
    IntegrationCallbackUrlDocument,
    IntegrationEnvironmentOverrideAllowedDocument,
    IntegrationProvidersDocument,
    IntegrationsDocument,
    SetIntegrationSecretDocument,
    StartIntegrationConnectionDocument,
    TestIntegrationConnectionDocument,
    UpdateIntegrationDocument,
} from "@/graphql/integrations.generated";
import { OrganizationOptionsDocument } from "@/graphql/organizations.generated";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { formatDateTime } from "../../../global/formatHelpers";

/**
 * Admin-only management of external system integrations.
 *
 * Deliberately provider-agnostic: the provider list, its environments and its
 * capabilities all come from `integrationProviders`, so a new backend adapter
 * shows up here with no UI change. Credentials are write-only — the API only
 * ever reports whether a secret/token exists and when it expires.
 */

type StatusTone = "connected" | "error" | "pending";

/**
 * What the integration's state actually is, in one phrase.
 *
 * `status` on the row only knows what the last server action concluded, which isn't
 * enough: a row can be "CONFIGURED" while missing the client id, the secret, or
 * both, and each of those is a different next step. So the badge is derived from
 * everything we know and names the blocker, ordered most-blocking first — an admin
 * should never have to assemble this from separate readouts.
 */
type IntegrationState = { label: string; tone: StatusTone };

const describeStatus = (
    integration: { status: string; integrationClientId?: string | null },
    credentials: { hasClientSecret: boolean; hasAccessToken: boolean; expired: boolean },
): IntegrationState => {
    const missingId = !integration.integrationClientId;
    const missingSecret = !credentials.hasClientSecret;

    if (missingId && missingSecret) return { label: "Missing credentials", tone: "pending" };
    if (missingId) return { label: "Missing client ID", tone: "pending" };
    if (missingSecret) return { label: "Missing client secret", tone: "pending" };
    // Expiry is checked before ERROR: a stale token explains most failures, and
    // "needs refresh" is a step, where "error" is only a symptom.
    if (credentials.expired) return { label: "Needs refresh", tone: "error" };
    if (integration.status === "ERROR") return { label: "Error", tone: "error" };
    if (!credentials.hasAccessToken) return { label: "Not connected", tone: "pending" };
    return { label: "Connected", tone: "connected" };
};

const TONE_CLASS: Record<StatusTone, string> = {
    connected: "border-success/30 bg-success/10 text-success",
    error: "border-destructive/30 bg-destructive/10 text-destructive",
    pending: "border-border bg-muted text-muted-foreground",
};

function StatusPill({ state }: { state: IntegrationState }) {
    const Icon = state.tone === "connected" ? CheckCircle2 : state.tone === "error" ? TriangleAlert : Plug;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${TONE_CLASS[state.tone]}`}
        >
            <Icon className="h-3.5 w-3.5" />
            {state.label}
        </span>
    );
}

/**
 * A labelled fact about the integration. Four of these read far better than one
 * run-on line of monospace, which is the one thing the old admin got right.
 */
const Tile = ({ label, value, detail }: { label: string; value: string; detail?: string | null }) => (
    <div className="border-border bg-muted/40 rounded-lg border p-3">
        <Eyebrow className="text-[10px]">{label}</Eyebrow>
        <Text weight="semibold" className="mt-0.5 text-sm">
            {value}
        </Text>
        {detail ? <Mono className="text-muted-foreground mt-0.5 block text-[11px] break-all">{detail}</Mono> : null}
    </div>
);

/**
 * Turns a provider rejection into the field to fix and what to do about it.
 *
 * The raw text is the provider's own error code. Each of these means a specific
 * thing to go change, and none of them are self-evident from the code alone —
 * `redirect_uri_mismatch` in particular reads like our bug when it is a missing
 * entry in the connected app.
 */
const describeConnectFailure = (
    raw: string,
    callbackUrl?: string,
): { message: string; fields: ("clientId" | "secret")[] } => {
    const text = raw.toLowerCase();

    // Ours, not the provider's: the API could not work out its own public origin.
    if (text.includes("determine the api origin")) {
        return {
            fields: [],
            message:
                "This deployment can't work out its own callback URL, so it never reached the provider. Set INTEGRATIONS_API_ORIGIN (locally, `yarn dev:api` does it) and try again.",
        };
    }
    if (text.includes("redirect_uri")) {
        return {
            fields: ["clientId"],
            message: `The provider does not accept this app's callback URL. Open the connected app for this Client ID (Setup → App Manager, or External Client Apps → OAuth Settings) and add this exact URL to its Callback URL list${
                callbackUrl ? `: ${callbackUrl}` : ""
            }`,
        };
    }
    if (text.includes("invalid_client_id") || text.includes("client identifier")) {
        return {
            fields: ["clientId"],
            message:
                "The provider does not have an app with this Client ID in this environment. Re-copy the consumer key from the connected app.",
        };
    }
    if (text.includes("invalid_client") || text.includes("client credentials")) {
        return {
            fields: [
                "clientId",
                "secret",
            ],
            message:
                "The provider rejected this key and secret pair. Re-copy both from the connected app — the secret is the long numeric one, not the key. Also check that the app does not require PKCE.",
        };
    }
    if (text.includes("invalid_grant")) {
        return {
            fields: [],
            message: "The authorization expired before it was exchanged. Try connecting again.",
        };
    }
    if (text.includes("access_denied") || text.includes("denied")) {
        return { fields: [], message: "Consent was declined in the provider." };
    }
    return { fields: [], message: raw || "The provider rejected the connection." };
};

export function IntegrationsPage() {
    const { data: providerData } = useQuery(IntegrationProvidersDocument);
    const { data, loading, error, refetch } = useQuery(IntegrationsDocument);
    const { data: orgData } = useQuery(OrganizationOptionsDocument);
    // ORG_ADMINs reach this page for their own org, so admin-only controls are gated
    // on the caller's role here as well as on the server (@auth on the mutations).
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const isPlatformAdmin =
        currentUserData?.currentUser?.role === "ADMIN" || currentUserData?.currentUser?.role === "SYSTEM";
    // Prod fixes the environment; lower deployments let an admin override it.
    const { data: overrideData } = useQuery(IntegrationEnvironmentOverrideAllowedDocument);
    // Both must hold: a non-prod deployment, and a platform admin looking at it.
    const overrideAllowed = (overrideData?.integrationEnvironmentOverrideAllowed ?? false) && isPlatformAdmin;
    // Must be registered verbatim on the provider's app, and it is derived per
    // stage AND per provider — so show it rather than making an admin build it.
    const firstProvider = providerData?.integrationProviders[0]?.id;
    const { data: callbackData } = useQuery(IntegrationCallbackUrlDocument, {
        variables: { provider: firstProvider as never },
        skip: !firstProvider,
    });
    const callbackUrl = callbackData?.integrationCallbackUrl;

    const [
        createIntegration,
        { loading: creating },
    ] = useMutation(CreateIntegrationDocument);
    const [deleteIntegration] = useMutation(DeleteIntegrationDocument);
    const [setSecret] = useMutation(SetIntegrationSecretDocument);
    const [updateIntegration] = useMutation(UpdateIntegrationDocument);
    const [startConnection] = useMutation(StartIntegrationConnectionDocument);
    const [testConnection] = useMutation(TestIntegrationConnectionDocument);

    const providers = providerData?.integrationProviders ?? [];

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        providerFilter,
        setProviderFilter,
    ] = useState<string>(ALL);
    const [
        statusFilter,
        setStatusFilter,
    ] = useState<string>(ALL);
    const integrations = data?.integrations ?? [];

    const [
        busyId,
        setBusyId,
    ] = useState<string | null>(null);
    const [
        connectionLink,
        setConnectionLink,
    ] = useState<string | null>(null);
    const [
        showSecret,
        setShowSecret,
    ] = useState(false);
    /** Which integration's data is being viewed below the grid. */
    const [
        selectedId,
        setSelectedId,
    ] = useState<string | null>(null);
    /** Integration awaiting delete confirmation. */
    const [
        deleting,
        setDeleting,
    ] = useState<string | null>(null);

    /**
     * The one configuration form, used for both adding and editing.
     *
     * Setup used to be four steps with three buttons — create, save the secret,
     * connect — each able to fail on its own and leave a half-built integration
     * behind. It is one form and one Connect now: everything the provider gave you
     * goes in, and the single button carries it all the way through consent.
     */
    const [
        config,
        setConfig,
    ] = useState<{
        mode: "add" | "edit";
        integrationId?: string;
        organizationId: string;
        providerId: string;
        clientId: string;
        secret: string;
        hasSecret: boolean;
    } | null>(null);
    /** Failure from the last Connect, and which fields it points at. */
    const [
        configError,
        setConfigError,
    ] = useState<{ message: string; fields: ("clientId" | "secret")[] } | null>(null);
    /** True from clicking Connect until the provider's tab reports back. */
    const [
        connecting,
        setConnecting,
    ] = useState(false);

    // Environment override, shown to platform admins on non-prod deployments.
    const [
        overrideEnvironment,
        setOverrideEnvironment,
    ] = useState(false);
    const [
        environment,
        setEnvironment,
    ] = useState("");
    const [
        loginUrl,
        setLoginUrl,
    ] = useState("");

    const selectedProvider = providers.find((p) => p.id === config?.providerId);
    const environmentOptions = selectedProvider?.environments ?? providers[0]?.environments ?? [];
    const requiresLoginUrl = environmentOptions.find((e) => e.value === environment)?.requiresLoginUrl ?? false;

    // Open the only integration's records on arrival — ONCE. Re-running this on
    // every render is what made "Hide records" appear to do nothing: the effect
    // immediately re-selected what the click had just cleared.
    const autoSelected = useRef(false);
    useEffect(() => {
        if (autoSelected.current || integrations.length !== 1) return;
        autoSelected.current = true;
        setSelectedId(integrations[0]?.id ?? null);
    }, [integrations]);

    // Drop a selection whose integration is gone (deleted, or no longer listed).
    useEffect(() => {
        if (selectedId && !integrations.some((i) => i.id === selectedId)) setSelectedId(null);
    }, [
        integrations,
        selectedId,
    ]);

    /** One integration per org, so an org that has one is not offered a second. */
    const takenOrgIds = useMemo(() => integrations.map((i) => i.organizationId), [integrations]);
    /** Orgs still free to add — the count is what decides whether "Add" is offered at all. */
    const availableOrgs = useMemo(() => {
        const taken = new Set(takenOrgIds);
        return (orgData?.organizations ?? []).filter((o) => !taken.has(o.id));
    }, [
        orgData,
        takenOrgIds,
    ]);

    // The OAuth tab posts its outcome here when the provider redirects to our
    // callback; refetch so the card reflects the new connection state.
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            const data = event.data as { source?: string; status?: string; detail?: string } | null;
            if (data?.source !== "athena-integration-oauth") return;
            setConnecting(false);
            if (data.status === "connected") {
                // Worked: the form is done with, and the card now tells the story.
                setConfig(null);
                setConfigError(null);
                setShowSecret(false);
                toast.success("Integration connected.");
            } else {
                // Failed at the provider: keep the form up, naming the field to fix.
                setConfigError(describeConnectFailure(data.detail ?? "", callbackUrl));
            }
            void refetch();
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [
        refetch,
        callbackUrl,
    ]);

    // Fallback for the same signal: postMessage can be lost (opener severed, the
    // admin authorized in a different window, the tab was closed early). Refetch
    // when they come back to this page so the card is never stale.
    useEffect(() => {
        if (!connecting) return;
        const onFocus = () => void refetch();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [
        connecting,
        refetch,
    ]);

    // A typed-but-unsaved client secret is unrecoverable — the admin pasted it from
    // the provider and we never show it again. Warn before the tab takes it away.
    useEffect(() => {
        if (!config?.secret.trim()) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [config?.secret]);

    const closeConfig = () => {
        if (config?.secret.trim() && !window.confirm("Discard the unsaved client secret?")) return;
        setConfig(null);
        setConfigError(null);
        setShowSecret(false);
        setOverrideEnvironment(false);
        setEnvironment("");
        setLoginUrl("");
    };

    /** Opens the form for a brand-new integration. */
    const openAddConfig = () => {
        setConfigError(null);
        setShowSecret(false);
        setOverrideEnvironment(false);
        setEnvironment("");
        setLoginUrl("");
        setConfig({
            mode: "add",
            organizationId: "",
            providerId: providers[0]?.id ?? "",
            clientId: "",
            secret: "",
            hasSecret: false,
        });
    };

    const copyCallbackUrl = () => {
        if (!callbackUrl) return;
        void navigator.clipboard?.writeText(callbackUrl);
        toast.success("Callback URL copied. It must match the provider's app exactly.");
    };

    /**
     * Refreshes the integration list, swallowing a failed refresh.
     *
     * Every caller runs after the real work already succeeded, so a stale list is the entire
     * cost. Two reasons it must not throw: these handlers are fired and forgotten (`run(...)`
     * without a catch, and a bare `(async () => {})()` in the connect flow), so a rejection
     * becomes an unhandled promise rejection; and where a refetch did sit inside a catch, an
     * aborted refresh surfaced as "Connection test failed", blaming the operation that worked.
     * Apollo aborts in-flight queries when the component unmounts, so closing the page right
     * after acting was enough.
     */
    const refreshIntegrations = async () => {
        try {
            await refetch();
        } catch {
            // Deliberately ignored — see above.
        }
    };

    const run = async (id: string, action: () => Promise<void>) => {
        setBusyId(id);
        try {
            await action();
        } finally {
            setBusyId(null);
        }
    };

    /**
     * The whole of setup, in one action.
     *
     * Creates or updates the row, stores the secret if one was typed, asks the
     * provider for a consent URL and sends the tab there. Any failure leaves the
     * form open with the reason attached to the field that caused it; success is
     * reported later by the callback tab, which is what closes the form.
     */
    const handleConnectFromConfig = () => {
        if (!config) return;
        // Claimed inside the click: a window opened after an await is not
        // user-activated, and Safari refuses to navigate it.
        const tab = window.open("", "_blank");
        if (!tab) {
            setConfigError({
                fields: [],
                message: "Allow pop-ups for this site, or use Copy connection link to authorize in another tab.",
            });
            return;
        }

        setConfigError(null);
        setConnecting(true);
        void (async () => {
            try {
                let id = config.integrationId;
                const environmentInput =
                    overrideEnvironment && environment
                        ? { integrationEnvironment: environment, integrationLoginUrl: loginUrl.trim() || null }
                        : {};

                if (config.mode === "add") {
                    const created = await createIntegration({
                        variables: {
                            input: {
                                organizationId: config.organizationId,
                                provider: config.providerId as never,
                                integrationClientId: config.clientId.trim(),
                                ...environmentInput,
                            },
                        },
                    });
                    id = created.data?.createIntegration.id;
                    if (!id) throw new Error("The integration was not created");
                    // From here on this is an edit: the organization is settled and a
                    // retry must update rather than create a duplicate.
                    setConfig((current) => (current ? { ...current, mode: "edit", integrationId: id } : current));
                } else {
                    await updateIntegration({
                        variables: {
                            id: config.integrationId as string,
                            input: { integrationClientId: config.clientId.trim(), ...environmentInput },
                        },
                    });
                }

                // After the row, so a rejected client id can't strand a new secret.
                if (config.secret.trim()) {
                    await setSecret({ variables: { id: id as string, clientSecret: config.secret.trim() } });
                }

                const link = await startConnection({ variables: { id: id as string } });
                const url = link.data?.startIntegrationConnection.url;
                if (!url) throw new Error("The provider did not return a consent URL");

                setConfig((current) =>
                    current ? { ...current, mode: "edit", integrationId: id, secret: "", hasSecret: true } : current,
                );
                tab.location.href = url;
                await refreshIntegrations();
            } catch (error) {
                tab.close();
                setConnecting(false);
                setConfigError(
                    describeConnectFailure(error instanceof Error ? error.message : String(error), callbackUrl),
                );
                await refreshIntegrations();
            }
        })();
    };

    /**
     * Escape hatch for the popup: a plain link to the provider's consent screen.
     * Needed when pop-ups are blocked, and when the person holding the provider
     * credentials isn't the admin sitting in front of this page. The link carries
     * a 15-minute state token, so it has to be used promptly.
     */
    const handleCopyLink = (id: string) =>
        run(id, async () => {
            try {
                const result = await startConnection({ variables: { id } });
                const url = result.data?.startIntegrationConnection.url;
                if (!url) throw new Error("The provider did not return a consent URL");
                setConnectionLink(url);
                // Clipboard access can be denied; the dialog still shows the URL.
                await navigator.clipboard?.writeText(url).catch(() => undefined);
            } catch (error) {
                toast.error(error instanceof Error ? error.message : "Could not build the connection link");
            }
        });

    const handleTest = (id: string) =>
        run(id, async () => {
            try {
                const result = await testConnection({ variables: { id } });
                const payload = result.data?.testIntegrationConnection;
                if (payload?.ok) toast.success(payload.message);
                else toast.error(payload?.message ?? "Connection test failed");
                await refreshIntegrations();
            } catch (error) {
                toast.error(error instanceof Error ? error.message : "Connection test failed");
            }
        });

    const handleRemove = (id: string) =>
        run(id, async () => {
            try {
                await deleteIntegration({ variables: { id } });
                toast.success("Integration removed.");
                await refreshIntegrations();
            } catch (error) {
                toast.error(error instanceof Error ? error.message : "Could not remove the integration");
            }
        });

    /**
     * Filtered in the browser: `integrations` returns the caller's whole accessible set in one query,
     * so there is no page boundary for a search to fall off. Provider and status are what an operator
     * narrows by — "which Salesforce connections are in error" is the question this page exists for.
     */
    const visibleIntegrations = useMemo(() => {
        const term = search.trim().toLowerCase();
        return integrations.filter((integration) => {
            if (providerFilter !== ALL && integration.provider !== providerFilter) return false;
            if (statusFilter !== ALL && integration.status !== statusFilter) return false;
            // Organization name as well as provider: an operator arrives knowing the partner.
            return !term || `${integration.organization.name} ${integration.provider}`.toLowerCase().includes(term);
        });
    }, [
        integrations,
        search,
        providerFilter,
        statusFilter,
    ]);

    const filtersActive = search.trim() !== "" || providerFilter !== ALL || statusFilter !== ALL;

    /** Selected last, so it reads as the expanded one at the bottom of the grid. */
    const ordered = useMemo(
        () => [...visibleIntegrations].sort((a, b) => Number(a.id === selectedId) - Number(b.id === selectedId)),
        [
            visibleIntegrations,
            selectedId,
        ],
    );

    /** The organization the form is bound to, for the locked display in edit mode. */
    const configuredOrgName = config
        ? (integrations.find((i) => i.organizationId === config.organizationId)?.organization.name ??
          orgData?.organizations.find((o) => o.id === config.organizationId)?.name)
        : undefined;

    /** Everything the provider needs before consent is worth attempting. */
    const canConnect = Boolean(
        config &&
        (config.mode === "edit" || config.organizationId) &&
        config.providerId &&
        config.clientId.trim() &&
        // A brand-new integration has no stored secret to fall back on.
        (config.hasSecret || config.secret.trim()) &&
        (!overrideEnvironment || (environment && (!requiresLoginUrl || loginUrl.trim()))),
    );

    /**
     * One integration card, compact in the grid and expanded when its records are
     * showing — same markup either way so the two can't drift.
     *
     * The actions are deliberately unequal. At any moment there is exactly one thing
     * to do next (set the secret, connect, or verify), so that is the only filled
     * button. Everything else is either maintenance or destructive, and reads that
     * way: quiet text on a divided row, with the two irreversible ones last.
     */
    const renderCard = (integration: (typeof integrations)[number], expanded: boolean) => {
        const provider = providers.find((p) => p.id === integration.provider);
        const credentials = integration.credentialStatus;
        const busy = busyId === integration.id;

        const state = describeStatus(integration, credentials);
        const needsAttention = state.tone !== "connected";
        const openConfig = () => {
            setConfigError(null);
            setShowSecret(false);
            setOverrideEnvironment(false);
            setEnvironment(integration.integrationEnvironment ?? "");
            setLoginUrl(integration.integrationLoginUrl ?? "");
            setConfig({
                mode: "edit",
                integrationId: integration.id,
                organizationId: integration.organizationId,
                providerId: integration.provider,
                clientId: integration.integrationClientId ?? "",
                secret: "",
                hasSecret: credentials.hasClientSecret,
            });
        };

        const canOauth = provider?.capabilities.includes("OAUTH") ?? false;
        /**
         * The single next step, or none. Credentials first, then consent; once it is
         * connected there is nothing to push, so no filled button competes with the
         * icon controls.
         */
        const primary = !credentials.hasClientSecret
            ? { label: "Add credentials", Icon: KeyRound, run: openConfig }
            : !credentials.hasAccessToken && canOauth
              ? { label: "Connect", Icon: Link2, run: openConfig }
              : null;

        return (
            <Card
                key={integration.id}
                width={expanded ? "full" : "fixed"}
                className={expanded ? "ring-primary ring-2" : undefined}
            >
                <CardContent className="flex flex-col gap-4 p-5">
                    {/* Status, then the three things you can do to the connection
                        itself: verify it, edit its credentials, delete it. Icons, so
                        they read as controls without competing with the next step. */}
                    <div className="flex items-center justify-between gap-3">
                        <Eyebrow>{provider?.label ?? integration.provider}</Eyebrow>
                        <div className="flex items-center gap-2">
                            <StatusPill state={state} />
                            <Button
                                size="icon-sm"
                                variant="outline"
                                title="Test connection"
                                aria-label="Test connection"
                                disabled={busy || !credentials.hasAccessToken}
                                onClick={() => void handleTest(integration.id)}
                            >
                                <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                size="icon-sm"
                                variant="outline"
                                // Anything but Connected means the credentials are the
                                // thing to look at, so the control says so.
                                className={needsAttention ? "border-destructive text-destructive" : undefined}
                                title="Credentials and configuration"
                                aria-label="Credentials and configuration"
                                disabled={busy}
                                onClick={openConfig}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                size="icon-sm"
                                variant="outline"
                                className="text-destructive"
                                title="Delete integration"
                                aria-label="Delete integration"
                                disabled={busy}
                                onClick={() => setDeleting(integration.id)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>

                    <div className="min-w-0">
                        <Text weight="semibold" className="text-base">
                            {integration.organization.name}
                        </Text>
                        {!expanded ? (
                            <Mono className="text-muted-foreground mt-1 block text-[11px]">
                                {isPlatformAdmin
                                    ? (integration.integrationEnvironment ?? "no environment")
                                    : (integration.integrationInstanceUrl ?? "not linked yet")}
                            </Mono>
                        ) : null}
                    </div>

                    {expanded ? (
                        <div
                            className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${isPlatformAdmin ? "lg:grid-cols-3" : ""}`}
                        >
                            {/* Which provider tenant this points at only means something
                                to whoever administers the platform. */}
                            {isPlatformAdmin ? (
                                <Tile
                                    label="Environment"
                                    value={`${integration.integrationEnvironment ?? "unset"} (auto)`}
                                    detail={integration.resolvedLoginUrl}
                                />
                            ) : null}
                            <Tile
                                label="Tenant"
                                value={integration.integrationInstanceUrl ? "Linked" : "Not linked yet"}
                                detail={integration.integrationInstanceUrl ?? integration.externalOrganizationId}
                            />
                            <Tile
                                label="Activity"
                                value={
                                    integration.connectedAt
                                        ? `Connected ${formatDateTime(integration.connectedAt)}`
                                        : "Never connected"
                                }
                                detail={
                                    integration.lastCheckedAt
                                        ? `Checked ${formatDateTime(integration.lastCheckedAt)}`
                                        : "Never checked"
                                }
                            />
                        </div>
                    ) : null}

                    {integration.lastError ? (
                        <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-xs break-words">
                            {integration.lastError}
                        </div>
                    ) : null}

                    {/* At most one filled button: the single next step. Connected means
                        there is nothing to do, so none is shown. */}
                    <div className="flex flex-wrap items-center gap-2">
                        {primary ? (
                            <Button size="sm" className="w-auto" disabled={busy} onClick={primary.run}>
                                <primary.Icon className="mr-1.5 h-3.5 w-3.5" />
                                {primary.label}
                            </Button>
                        ) : null}
                        <Button
                            size="sm"
                            variant="outline"
                            className="w-auto"
                            onClick={() => setSelectedId(expanded ? null : integration.id)}
                        >
                            <ChevronDown className={`mr-1.5 h-3.5 w-3.5 ${expanded ? "rotate-180" : ""}`} />
                            {expanded ? "Hide data" : "Show data"}
                        </Button>
                    </div>

                    {expanded ? (
                        <IntegrationRecordsPanel
                            organizationId={integration.organizationId}
                            providerLabel={provider?.label ?? integration.provider}
                        />
                    ) : null}
                </CardContent>
            </Card>
        );
    };

    return (
        <Container className="p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
                <SectionHeader
                    title="Integrations"
                    eyebrow="External systems"
                    description="Connect an organization to an external learning or CRM platform. Credentials are stored securely by Release Recovery and can never be read back once saved."
                />
                <div className="flex shrink-0 items-center gap-2">
                    <Button variant="outline" className="w-auto" disabled={!callbackUrl} onClick={copyCallbackUrl}>
                        <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
                        Copy callback URL
                    </Button>
                    <Button className="w-auto" onClick={openAddConfig} disabled={availableOrgs.length === 0}>
                        Add integration
                    </Button>
                </div>
            </div>

            {/* Stale error over live data: keeping the cards mounted means a failed
                refetch no longer replaces the page, so it has to say so in place. */}
            {error && data ? (
                <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
                    <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                    <span className="break-words">Refresh failed: {error.message}</span>
                    <Button size="sm" variant="ghost" className="ml-auto w-auto" onClick={() => void refetch()}>
                        Retry
                    </Button>
                </div>
            ) : null}

            {/* `loading` goes true on every refetch — including the focus refetch and the
                one after a backfill. Keyed on data instead, so a refresh updates the
                cards in place: swapping in skeletons unmounted the records panel, which
                reset its tab and filters and aborted its in-flight query mid-click. */}
            {loading && !data ? (
                <div className="flex flex-col gap-3">
                    <Skeleton className="h-32 rounded-xl" />
                    <Skeleton className="h-32 rounded-xl" />
                </div>
            ) : error && !data ? (
                <Card width="full">
                    <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                        <TriangleAlert className="text-destructive h-8 w-8" />
                        <Text weight="semibold">Could not load integrations</Text>
                        <Text className="text-muted-foreground max-w-md text-sm">{error.message}</Text>
                        <Button variant="outline" className="mt-2 w-auto" onClick={() => void refetch()}>
                            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                            Try again
                        </Button>
                    </CardContent>
                </Card>
            ) : integrations.length === 0 ? (
                <Card width="full">
                    <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                        <PlugZap className="text-muted-foreground h-8 w-8" />
                        <Text weight="semibold">No integrations yet</Text>
                        <Text className="text-muted-foreground max-w-md text-sm">
                            Add one to sync learners, courses and progress with an external system.
                        </Text>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <FilterBar
                        search={search}
                        onSearchChange={setSearch}
                        searchLabel="Search integrations"
                        searchPlaceholder="Search by organization or provider"
                        searchClassName="max-w-xs"
                        active={filtersActive}
                        onClear={() => {
                            setSearch("");
                            setProviderFilter(ALL);
                            setStatusFilter(ALL);
                        }}
                        count={{ shown: visibleIntegrations.length, total: integrations.length }}
                    >
                        <Select value={providerFilter} onValueChange={setProviderFilter}>
                            <SelectTrigger className="w-44" aria-label="Filter by provider">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All providers</SelectItem>
                                {providers.map((provider) => (
                                    <SelectItem key={provider.id} value={provider.id}>
                                        {provider.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-48" aria-label="Filter by connection status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All statuses</SelectItem>
                                {CONNECTION_STATUSES.map((status) => (
                                    <SelectItem key={status} value={status}>
                                        {CONNECTION_STATUS_LABEL[status]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FilterBar>

                    {/* A filtered set matching nothing has to say so, or an empty grid under a
                        populated search box reads as a failed load. */}
                    {filtersActive && visibleIntegrations.length === 0 && (
                        <Card width="full">
                            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                                <PlugZap className="text-muted-foreground h-8 w-8" />
                                <Text weight="semibold">No integrations match these filters</Text>
                            </CardContent>
                        </Card>
                    )}

                    {/* One grid, so selecting a card moves it rather than remounting it:
                        unselected cards stay compact in columns, the selected one is last
                        and spans the full width with its records directly beneath. */}
                    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 2xl:grid-cols-3">
                        {ordered.map((integration) => {
                            const selected = integration.id === selectedId;
                            return (
                                <div key={integration.id} className={selected ? "col-span-full" : undefined}>
                                    {renderCard(integration, selected)}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* One form for adding and editing: credentials in, Connect out. */}
            <Dialog open={config !== null} onOpenChange={(open) => !open && closeConfig()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{config?.mode === "add" ? "Add integration" : "Configuration"}</DialogTitle>
                        <DialogDescription>
                            Paste the client id and secret from the provider&apos;s connected app, then Connect. You
                            will be sent to the provider to approve access.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-col gap-4">
                        {/* Once the row exists the organization is settled — shown, but
                            no longer changeable, so the form keeps its context after a
                            failed Connect instead of losing the field entirely. */}
                        {config && config.mode === "edit" ? (
                            <div className="border-border bg-muted/40 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border px-3 py-2">
                                <div>
                                    <Eyebrow className="text-[10px]">Organization</Eyebrow>
                                    <Text weight="semibold" className="text-sm">
                                        {configuredOrgName ?? "—"}
                                    </Text>
                                </div>
                                <div>
                                    <Eyebrow className="text-[10px]">Provider</Eyebrow>
                                    <Text weight="semibold" className="text-sm">
                                        {providers.find((p) => p.id === config.providerId)?.label ?? config.providerId}
                                    </Text>
                                </div>
                            </div>
                        ) : null}

                        {config?.mode === "add" ? (
                            <>
                                <div>
                                    <FieldLabel>Organization</FieldLabel>
                                    <OrganizationSelect
                                        value={config.organizationId}
                                        onValueChange={(value) =>
                                            setConfig((current) =>
                                                current ? { ...current, organizationId: value } : current,
                                            )
                                        }
                                        placeholder="Select an organization"
                                        aria-label="Organization"
                                        excludeIds={takenOrgIds}
                                    />
                                </div>

                                <div>
                                    <FieldLabel>Provider</FieldLabel>
                                    <Select
                                        value={config.providerId}
                                        onValueChange={(value) =>
                                            setConfig((current) =>
                                                current ? { ...current, providerId: value } : current,
                                            )
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a provider" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {providers.map((provider) => (
                                                <SelectItem key={provider.id} value={provider.id}>
                                                    {provider.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        ) : null}

                        <div>
                            <FieldLabel>Client ID</FieldLabel>
                            <Input
                                value={config?.clientId ?? ""}
                                onChange={(e) =>
                                    setConfig((current) => (current ? { ...current, clientId: e.target.value } : null))
                                }
                                placeholder="Consumer key from the provider's connected app"
                                className={
                                    configError?.fields.includes("clientId")
                                        ? "border-destructive focus-visible:ring-destructive"
                                        : undefined
                                }
                            />
                        </div>

                        {/* Same shape as Client ID — label, field, then the note.
                            The note used to sit between the label and the input,
                            which made this field read as a different kind of thing. */}
                        <div>
                            <FieldLabel>Client secret</FieldLabel>
                            <div className="flex items-center gap-2">
                                <Input
                                    type={showSecret ? "text" : "password"}
                                    autoComplete="off"
                                    value={config?.secret ?? ""}
                                    onChange={(e) =>
                                        setConfig((current) =>
                                            current ? { ...current, secret: e.target.value } : null,
                                        )
                                    }
                                    placeholder={
                                        config?.hasSecret
                                            ? "Leave blank to keep the saved secret"
                                            : "Paste the client secret"
                                    }
                                    className={
                                        configError?.fields.includes("secret")
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : undefined
                                    }
                                />
                                {/* Matched to the input's h-10; size="icon" is h-9 and
                                    left the button floating inside the row. */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 shrink-0"
                                    aria-label={showSecret ? "Hide secret" : "Show secret"}
                                    onClick={() => setShowSecret((visible) => !visible)}
                                >
                                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>

                            {config?.hasSecret ? (
                                <div className="border-success/30 bg-success/10 mt-2 flex items-start gap-2 rounded-lg border px-3 py-2">
                                    <ShieldCheck className="text-success mt-0.5 h-4 w-4 shrink-0" />
                                    <Text className="text-xs">
                                        A secret is saved. It is stored securely by Release Recovery and can never be
                                        displayed again — not even to us — so there is no way to check it. Paste a new
                                        one above to replace it.
                                    </Text>
                                </div>
                            ) : (
                                <Text className="text-muted-foreground mt-1 text-xs">
                                    No secret saved yet. Paste the consumer secret from the provider&apos;s connected
                                    app — keep your own copy, this is the only place it can be entered, never read.
                                </Text>
                            )}
                        </div>

                        {isPlatformAdmin ? (
                            <div className="border-border bg-muted/40 flex flex-col gap-3 rounded-lg border p-3">
                                <Text className="text-xs">
                                    Environment: <span className="font-medium">{environment || "from deployment"}</span>{" "}
                                    <span className="text-muted-foreground">(auto)</span>
                                </Text>
                                {overrideAllowed ? (
                                    <div className="flex items-start gap-2.5">
                                        <Checkbox
                                            id="override-environment"
                                            checked={overrideEnvironment}
                                            onCheckedChange={(checked) => {
                                                setOverrideEnvironment(checked === true);
                                                if (checked !== true) setLoginUrl("");
                                            }}
                                        />
                                        <div>
                                            <label htmlFor="override-environment" className="text-sm font-medium">
                                                Override environment (admin-only)
                                            </label>
                                            <Text className="text-muted-foreground text-xs">
                                                Available because this is not the production deployment.
                                            </Text>
                                        </div>
                                    </div>
                                ) : null}

                                {overrideEnvironment ? (
                                    <>
                                        <Select value={environment} onValueChange={setEnvironment}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select an environment" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {environmentOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {requiresLoginUrl ? (
                                            <Input
                                                value={loginUrl}
                                                onChange={(e) => setLoginUrl(e.target.value)}
                                                placeholder="https://your-domain.my.salesforce.com"
                                            />
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                        ) : null}

                        {/* The provider's reason, with the callback URL to hand when
                            that is what it is complaining about. */}
                        {configError ? (
                            <div className="border-destructive/30 bg-destructive/10 flex flex-col gap-2 rounded-lg border px-3 py-2">
                                <div className="flex items-start gap-2">
                                    <TriangleAlert className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
                                    <Text className="text-destructive text-xs">{configError.message}</Text>
                                </div>
                                {configError.fields.includes("clientId") && callbackUrl ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-auto self-start"
                                        onClick={copyCallbackUrl}
                                    >
                                        <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
                                        Copy callback URL
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}

                        {connecting ? (
                            <Text className="text-muted-foreground text-xs">
                                Waiting for the provider… approve access in the tab that opened. This stays open until
                                it reports back.
                            </Text>
                        ) : null}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeConfig}>
                            Cancel
                        </Button>
                        {config?.mode === "edit" && config.hasSecret ? (
                            <Button
                                variant="outline"
                                className="w-auto"
                                disabled={connecting}
                                onClick={() => void handleCopyLink(config.integrationId as string)}
                            >
                                <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
                                Copy connection link
                            </Button>
                        ) : null}
                        <Button disabled={!canConnect || connecting || creating} onClick={handleConnectFromConfig}>
                            <Link2 className="mr-1.5 h-3.5 w-3.5" />
                            {connecting ? "Connecting…" : "Connect"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete is the only destructive action; it takes the credentials with it */}
            <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete this integration?</DialogTitle>
                        <DialogDescription>
                            Removes the configuration, the stored credentials and every record mapping. Nothing is
                            changed in the provider — its records stay where they are. Setting it up again means
                            entering the client id and secret from scratch.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleting(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (!deleting) return;
                                const id = deleting;
                                setDeleting(null);
                                void handleRemove(id);
                            }}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Connection link — the popup-free path through the provider's consent screen */}
            <Dialog open={connectionLink !== null} onOpenChange={(open) => !open && setConnectionLink(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Connection link</DialogTitle>
                        <DialogDescription>
                            Copied to your clipboard. Open it in any browser signed in to the provider — it does not
                            require admin access to this app. The link expires in 15 minutes.
                        </DialogDescription>
                    </DialogHeader>
                    <Input readOnly value={connectionLink ?? ""} onFocus={(e) => e.target.select()} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConnectionLink(null)}>
                            Close
                        </Button>
                        <Button
                            onClick={() => {
                                if (connectionLink) window.open(connectionLink, "_blank", "noopener,noreferrer");
                            }}
                        >
                            Open now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Container>
    );
}
